import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useGlobalContext } from 'GlobalContext';
import { useModal } from 'components/modals/ModalContext';
import { useAllowedRight } from 'services/permissions/permissionChecks';
import { saveData } from 'services/api/saveData';
import LinkItemModal from 'components/modals/LinkItemModal';
import IconButton from 'components/buttons/IconButton';
import Tooltip from 'components/alerts/Tooltip';
import ContactForm from '../../views/contacts/ContactForm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faPlus, faXmark } from '@fortawesome/free-solid-svg-icons';
import '../../style/scss/card.scss';

interface PersonOrganizationCardProps {
    itemId: string;
    data?: any;
    loadStatus?: 'idle' | 'loading' | 'loaded';
    isEditable?: boolean;
    refetchData?: () => void;
}

/*
 * PersonOrganizationCard.tsx
 * Shows the organization a contact person belongs to with a link to
 * its page, and lets the user link the person to an organization or
 * unlink it. A person that is a client itself never shows this card.
 */
const PersonOrganizationCard: React.FC<PersonOrganizationCardProps> = ({
    itemId, data, loadStatus, isEditable = true, refetchData
}) => {
    const { t } = useTranslation();
    const { setFloatingAlert } = useGlobalContext();
    const { initializeModal } = useModal();
    const [userHasOnlyViewRights] = useState<boolean>(useAllowedRight('only_view'));

    // A person that is a client itself is never a contact person
    if (data?.contact_type === 'client') {
        return null;
    }

    // Get the organization from the page data
    const organization: { id: number, name: string } | null = data?.organization ?? null;
    const canEdit = isEditable && !userHasOnlyViewRights;

    // Open the modal linking this person to an existing or a newly added organization
    const openLinkModal = () => {
        initializeModal(
            <LinkItemModal
                itemId={parseInt(itemId)}
                searchFieldConfig={{
                    name: 'organization',
                    placeholder: 'organization.general.search_organization_label',
                    objectName: 'organization.general.object_name.singular',
                    endpoint: 'get_contact_search',
                    params: { entity_type: 'organization' },
                    addNewItem: { modalForm: <ContactForm linkedItem={{ entity_type: 'organization' }} /> },
                }}
                submitConfig={{ endpoint: 'patch_person', apiField: 'organization' }}
                refetchData={() => refetchData && refetchData()} />,
            { title: t('person.general.link_organization_label'), modalSize: 'small' }
        );
    };

    // Unlink the person from its organization
    const handleUnlink = async () => {
        await saveData({ apiUrl: `patch_person/${itemId}`, method: 'patch', data: { organization: null } });
        if (refetchData) refetchData();
        setFloatingAlert({ type: 'success' });
    };

    return (
        <div className='card linked-items-card person-organization-card'>
            <div className='header'>
                <h6>{t('person.general.organization_card_label')}</h6>
                {canEdit && !organization && (
                    <IconButton
                        onClick={openLinkModal}
                        size='extra-small'
                        icon={<FontAwesomeIcon icon={faPlus} />}
                        tooltipText={t('person.general.link_organization_label')}
                    />
                )}
            </div>
            {loadStatus === 'loading' ? (
                <div className='loader'></div>
            ) : organization ? (
                <div className='card-list-item related-organization-row'>
                    <div>
                        <span className='related-organization-icon'>
                            <FontAwesomeIcon icon={faBuilding} />
                        </span>
                        <Link className='card-link' to={`/contacts/organization/${organization.id}`}>
                            {organization.name}
                        </Link>
                    </div>
                    {canEdit && (
                        <div className='unlink-icon tooltip-icon'>
                            <FontAwesomeIcon icon={faXmark} onClick={handleUnlink} />
                            <Tooltip>{t('forms.unlink_label')}</Tooltip>
                        </div>
                    )}
                </div>
            ) : (
                <p className='related-organizations-empty'>{t('person.general.no_organization_label')}</p>
            )}
        </div>
    );
};

export default PersonOrganizationCard;
